import { useState } from "react";
import { Badge } from "@/components/ui/PageHeader";
import { useAuth } from "@/context/AuthContext";
import * as n from "@/api/nostalgia";
import type { BoardItem } from "@/api/nostalgia";

// ═══════════════════════════════════════════════════════════════════
// The three things you do TO a board rather than in it: react to it, change
// how it is laid out, and rearrange what is on it.
//
// Two contracts worth seeing work:
//
//   · Reacting twice is not an error. The result carries `action: 'added' |
//     'exists'`, so a double tap is idempotent rather than a 409 — which is
//     the right call for something a thumb does on a scrolling list.
//   · Removing a reaction names the TYPE IN THE PATH. You take back one kind
//     of reaction, not "your reaction" — a board you called both `so_true` and
//     `feels` keeps the other one.
//
// Reorder sends the WHOLE list of positions in one call, not a swap. The
// server replies with how many rows it actually moved, which is the number
// worth showing: it is how you find out a reorder silently did nothing.
// ═══════════════════════════════════════════════════════════════════

const LAYOUTS = ["grid", "collage", "story", "free"] as const;

export default function BoardControls({
  boardId,
  items,
  currentLayout,
  onChanged,
}: {
  boardId: string;
  items: BoardItem[];
  currentLayout?: string | null;
  onChanged: () => void;
}) {
  const { isAuthenticated } = useAuth();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Which reactions this session added — the board response has no per-user
  // reaction field, so the toggle is local and honest about being local.
  const [mine, setMine] = useState<Set<string>>(new Set());

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      setNote(await fn());
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  }

  if (!isAuthenticated) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      <div>
        <p className="text-sm font-medium text-gray-900 mb-1">React</p>
        <p className="text-xs text-gray-500 mb-2">
          Tapping twice is not an error — the server answers <code>added</code>{" "}
          or <code>exists</code>. Removing names the type in the path, so one
          kind comes off and the others stay.
        </p>
        <div className="flex gap-1.5 flex-wrap">
          {n.BOARD_REACTIONS.map((r) => {
            const on = mine.has(r);
            return (
              <button
                key={r}
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    if (on) {
                      await n.removeBoardReaction(boardId, r);
                      setMine((s) => {
                        const next = new Set(s);
                        next.delete(r);
                        return next;
                      });
                      return `Took back "${r.replace(/_/g, " ")}".`;
                    }
                    const res = await n.reactToBoard(boardId, r);
                    setMine((s) => new Set(s).add(r));
                    return res.data.action === "exists"
                      ? `Already had "${r.replace(/_/g, " ")}" — said so rather than failing.`
                      : `Added "${r.replace(/_/g, " ")}".`;
                  })
                }
                className={`text-xs px-2.5 py-1 rounded-full border disabled:opacity-50 ${
                  on ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-700"
                }`}
              >
                {r.replace(/_/g, " ")}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-gray-900 mb-1">Layout</p>
        <div className="flex gap-1.5 flex-wrap items-center">
          {LAYOUTS.map((l) => (
            <button
              key={l}
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const res = await n.updateBoardLayout(boardId, l);
                  return `Now laid out as ${res.data.layoutMode}.`;
                })
              }
              className={`text-xs px-2.5 py-1 rounded-full border disabled:opacity-50 ${
                currentLayout === l
                  ? "bg-gray-900 text-white border-gray-900"
                  : "border-gray-300 text-gray-700"
              }`}
            >
              {l}
            </button>
          ))}
          {currentLayout && <Badge variant="gray">current: {currentLayout}</Badge>}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-gray-900 mb-1">Order</p>
        <p className="text-xs text-gray-500 mb-2">
          Sends every position in one call, not a swap. The reply says how many
          rows actually moved — which is how you find out a reorder did nothing.
        </p>
        <button
          disabled={busy || items.length < 2}
          onClick={() =>
            run(async () => {
              // Reverse is the cheapest change that is unmistakably visible.
              const res = await n.reorderBoardItems(
                boardId,
                [...items].reverse().map((it, i) => ({ id: it.id, sortOrder: i })),
              );
              return `Reversed — ${res.data.updatedCount} moved.`;
            })
          }
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50"
        >
          Reverse the order ({items.length} items)
        </button>
      </div>

      {note && <p className="text-sm text-green-700">{note}</p>}
      {err && <p className="text-sm text-red-700">{err}</p>}
    </div>
  );
}
